import { Box, List, Paper } from "@mui/material";
import type { FC, ReactNode } from "react";
import { matchPath } from "react-router-dom";
import { DevelopmentEnum } from "src/types/enum/DevelopmentEnum";
import NavItem from "./NavItem";

interface NavBarProps {
    className?: string;
    sections: NavBarSection[];
}

interface Item {
    href?: string;
    icon?: React.ElementType;
    info?: ReactNode;
    items?: Item[];
    title: string;
    disabled?: boolean;
    isProdAccessDisallowed?: boolean;
}

export interface NavBarSection {
    items: Item[];
}
function renderNavItems({
    items,
    pathname,
    depth = 0,
}: {
    items: Item[];
    pathname: string;
    depth?: number;
}) {
    return (
        <List disablePadding>
            {items.reduce((acc, item) => reduceChildRoutes({ acc, item, pathname, depth }), [])}
        </List>
    );
}

function reduceChildRoutes({
    acc,
    pathname,
    item,
    depth,
}: {
    acc: any[];
    pathname: string;
    item: Item;
    depth: number;
}) {
    const key = item.title + depth;
    const flyntEnv = import.meta.env.VITE_FLYNT_ENV;

    if (
        item.isProdAccessDisallowed &&
        !Object.values(DevelopmentEnum).includes(flyntEnv as DevelopmentEnum)
    ) {
        return acc;
    } else {
        if (item.items) {
            const open = matchPath(pathname, {
                path: item.href,
                exact: false,
            });

            acc.push(
                <NavItem
                    depth={depth}
                    icon={item.icon}
                    info={item.info}
                    key={key}
                    open={Boolean(open)}
                    title={item.title}
                >
                    {renderNavItems({
                        depth: depth + 1,
                        pathname,
                        items: item.items,
                    })}
                </NavItem>
            );
        } else {
            acc.push(
                <NavItem
                    disabled={item.disabled}
                    depth={depth}
                    href={item.href}
                    icon={item.icon}
                    info={item.info}
                    key={key}
                    title={item.title}
                />
            );
        }
        return acc;
    }
}

const NavBar: FC<NavBarProps> = ({ className, sections }) => {
    return (
        <Paper className={className}>
            <Box p={2}>
                {sections.map((section, index) => (
                    <List key={index}>
                        {renderNavItems({
                            items: section.items,
                            pathname: location.pathname,
                        })}
                    </List>
                ))}
            </Box>
        </Paper>
    );
};

export default NavBar;
